/**
 * Trang Danh sách vụ án (Đương sự / nội bộ) – dùng RTK Query, PageDataState.
 */
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Add, Visibility, FolderOff, ChevronRight } from '@mui/icons-material'
import { brandColors } from '../../theme'
import { PageDataState } from '../../components/PageDataState'
import { EmptyState } from '../../components/EmptyState'
import { useAppSelector } from '../../hooks/redux'
import { useGetCasesQuery } from '../../store/api/casesApi'
import type { Case, CaseStatus } from '../../types/case'

/** 5 dòng demo trên Bảng điều khiển (embedded) — không gọi API cho bảng này, không scroll. */
const EMBEDDED_DUMMY_CASES: Case[] = [
  {
    id: 'embed-d1',
    case_code: 'HN-GD-2026-001',
    track: 'A',
    category: 'marriage_family',
    status: 'accepted',
    claim_value_vnd: 150_000_000,
    plaintiff_id: 'user-1',
    defendant_id: 'user-2',
    plaintiff_name: 'Lê Hoàng Nam',
    defendant_name: 'Nguyễn Thị Mai Linh',
    filed_at: '2026-02-01T08:00:00Z',
    created_at: '2026-01-28T10:00:00Z',
    updated_at: '2026-01-28T10:00:00Z',
    created_by: 'system',
  },
  {
    id: 'embed-d2',
    case_code: 'HN-GD-2026-002',
    track: 'B',
    category: 'marriage_family',
    status: 'pre_trial',
    claim_value_vnd: 80_000_000,
    plaintiff_id: 'user-1',
    defendant_id: 'user-2',
    plaintiff_name: 'Trần Văn An',
    defendant_name: 'Phạm Thị B',
    filed_at: '2026-02-15T09:00:00Z',
    created_at: '2026-02-10T10:00:00Z',
    updated_at: '2026-02-10T10:00:00Z',
    created_by: 'system',
  },
  {
    id: 'embed-d3',
    case_code: 'DS-2026-004',
    track: 'A',
    category: 'civil',
    status: 'trial',
    claim_value_vnd: 320_000_000,
    plaintiff_id: 'user-1',
    defendant_id: 'user-2',
    plaintiff_name: 'Công ty TNHH X',
    defendant_name: 'Công ty CP Y',
    filed_at: '2026-01-20T08:30:00Z',
    created_at: '2026-01-18T10:00:00Z',
    updated_at: '2026-01-18T10:00:00Z',
    created_by: 'system',
  },
  {
    id: 'embed-d4',
    case_code: 'DD-2026-005',
    track: 'C',
    category: 'land',
    status: 'filed',
    claim_value_vnd: 900_000_000,
    plaintiff_id: 'user-1',
    defendant_id: 'user-2',
    plaintiff_name: 'Võ Thị Lan',
    defendant_name: 'Hoàng Văn Hùng',
    filed_at: '2026-03-01T10:00:00Z',
    created_at: '2026-02-28T10:00:00Z',
    updated_at: '2026-02-28T10:00:00Z',
    created_by: 'system',
  },
  {
    id: 'embed-d5',
    case_code: 'HN-GD-2026-006',
    track: 'A',
    category: 'marriage_family',
    status: 'closed',
    claim_value_vnd: 45_000_000,
    plaintiff_id: 'user-1',
    defendant_id: 'user-2',
    plaintiff_name: 'Đinh Minh K',
    defendant_name: 'Bùi Thị L',
    filed_at: '2025-11-10T08:00:00Z',
    created_at: '2025-11-08T10:00:00Z',
    updated_at: '2026-01-05T10:00:00Z',
    created_by: 'system',
  },
]

const statusLabels: Record<CaseStatus, string> = {
  draft: 'Nháp',
  filed: 'Đã nộp',
  accepted: 'Đã thụ lý',
  pre_trial: 'Chuẩn bị xét xử',
  trial: 'Đang xét xử',
  closed: 'Đã kết thúc',
  returned: 'Trả đơn',
  transferred: 'Chuyển đơn',
}

const statusBadge: Record<CaseStatus, { bg: string; color: string }> = {
  draft: { bg: '#9e9e9e', color: '#fff' },
  filed: { bg: brandColors.primary, color: '#fff' },
  accepted: { bg: '#2e7d32', color: '#fff' },
  pre_trial: { bg: '#ed6c02', color: '#fff' },
  trial: { bg: '#9c27b0', color: '#fff' },
  closed: { bg: '#2e7d32', color: '#fff' },
  returned: { bg: '#d32f2f', color: '#fff' },
  transferred: { bg: '#ed6c02', color: '#fff' },
}

const trackLabels: Record<string, string> = { A: 'Online', B: 'Hybrid', C: 'Trực tiếp' }

function formatClaimValue(vnd: number): string {
  if (vnd >= 1e9) return `${(vnd / 1e9).toFixed(1)} tỷ`
  return `${(vnd / 1e6).toFixed(0)} triệu`
}

export default function CasesPage({
  embedded = false,
  hideTitle = false,
  hideTopActions = false,
}: {
  embedded?: boolean
  /** Ẩn tiêu đề khi Bảng điều khiển đã có heading ngoài */
  hideTitle?: boolean
  /** Ẩn hàng nút (vd. nút đã đặt cùng hàng với tiêu đề section trên Bảng điều khiển) */
  hideTopActions?: boolean
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const user = useAppSelector((s) => s.auth.user)
  const { data: rawCases = [], isLoading, error, refetch } = useGetCasesQuery()
  // UC-L02: Đương sự chỉ thấy vụ án của mình (nguyên đơn)
  const casesFromApi =
    user?.role === 'user' || user?.role === 'litigant'
      ? rawCases.filter((c) => c.plaintiff_id === user?.id)
      : rawCases
  const cases = embedded ? EMBEDDED_DUMMY_CASES : casesFromApi
  const showLoading = embedded ? false : isLoading
  const showError = embedded ? undefined : error
  const showEmpty = embedded ? false : !isLoading && casesFromApi.length === 0

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {!hideTopActions && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: hideTitle ? 'flex-end' : 'space-between',
            alignItems: 'center',
            mb: embedded ? 2 : { xs: 2, sm: 3 },
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {!hideTitle && (
            <Typography
              variant={embedded ? 'subtitle1' : 'h5'}
              fontWeight={600}
              sx={{ fontSize: embedded ? '1rem' : { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' } }}
            >
              Vụ án của tôi
            </Typography>
          )}
          <Button
            variant="contained"
            size={isMobile ? 'medium' : 'medium'}
            startIcon={<Add />}
            onClick={() => navigate('/cases/new')}
            sx={{ borderRadius: 2, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
          >
            Nộp đơn khởi kiện
          </Button>
        </Box>
      )}

      <PageDataState
        isLoading={showLoading}
        error={showError}
        empty={showEmpty}
        emptyContent={
          <EmptyState
            icon={<FolderOff sx={{ fontSize: 40 }} />}
            title="Chưa có vụ án nào"
            description="Nộp đơn khởi kiện để bắt đầu theo dõi vụ án và nhận thông báo tiến trình."
            actionLabel="Nộp đơn khởi kiện"
            onAction={() => navigate('/cases/new')}
          />
        }
        onRetry={() => refetch()}
      >
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {cases.map((c) => (
              <Card
                key={c.id}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:active': { bgcolor: 'action.hover' },
                }}
                onClick={() => navigate(`/cases/${c.id}`)}
              >
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.875rem' }} noWrap>
                        {c.case_code}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                        <Chip
                          label={statusLabels[c.status]}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.75rem',
                            bgcolor: statusBadge[c.status].bg,
                            color: statusBadge[c.status].color,
                            border: 'none',
                          }}
                        />
                        <Chip label={trackLabels[c.track] ?? c.track} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem' }}>
                        {formatClaimValue(c.claim_value_vnd)} · {c.filed_at ? new Date(c.filed_at).toLocaleDateString('vi-VN') : '–'}
                      </Typography>
                    </Box>
                    <ChevronRight color="action" sx={{ flexShrink: 0 }} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Card sx={{ borderRadius: 2, boxShadow: 1, width: '100%', overflow: 'hidden' }}>
            <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
              <Table size="medium" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.8125rem' }, py: 1.5 }}>Mã vụ án</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.8125rem' }, py: 1.5 }}>Phân luồng</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.8125rem' }, py: 1.5 }}>Trạng thái</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.8125rem' }, py: 1.5 }}>Giá trị</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.8125rem' }, py: 1.5 }}>Ngày thụ lý</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.8125rem' }, py: 1.5 }}>Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cases.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{c.case_code}</TableCell>
                      <TableCell>
                        <Chip label={trackLabels[c.track] ?? c.track} size="small" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[c.status]}
                          size="small"
                          sx={{ bgcolor: statusBadge[c.status].bg, color: statusBadge[c.status].color, border: 'none', fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{formatClaimValue(c.claim_value_vnd)}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>{c.filed_at ? new Date(c.filed_at).toLocaleDateString('vi-VN') : '–'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/cases/${c.id}`)} sx={{ fontSize: '0.8125rem' }}>
                          Chi tiết
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </PageDataState>
    </Box>
  )
}
